import { useMemo, useState, useCallback } from 'react'
import { X, Plus, ShoppingCart, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Button,
  Badge,
  Separator,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  addItem,
  removeItem,
  updateQuantity,
  clearCart,
  closeCart,
  selectCartItems,
  selectCartTotals,
  selectIsCartOpen,
} from '@/store/cartSlice'
import { formatPrice } from '@/utils/formatting'
import { menuWidgetApi } from '@/store/convenienceAccessor'
import { getEnvironmentConfig } from '@/config/environment'
import { CartItem } from './CartItem'

interface CartSidebarProps {
  onProceedToCheckout?: () => void
}

export function CartSidebar({ onProceedToCheckout }: CartSidebarProps) {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(selectIsCartOpen)
  const items = useAppSelector(selectCartItems)
  const totals = useAppSelector(selectCartTotals)
  const config = getEnvironmentConfig()
  const { data: menuConfig } = menuWidgetApi.menuConfig.get({ restaurant_id: config.restaurantId })

  // Extract simple items only (no customization) for suggestions
  interface SuggestionItem {
    id: string
    name: string
    price: number
    currency?: string
    image_url?: string
  }

  const suggestionItems = useMemo((): SuggestionItem[] => {
    if (!menuConfig?.menus?.[0]?.sections) return []

    const cartItemIds = new Set(items.map((i) => i.id))

    interface SectionItem {
      id: string
      title: string
      price?: string
      base_price?: string
      currency?: string
      product_image_url?: string
      product_type?: string
      product_id?: string
      variation_price_overrides?: unknown
    }

    return menuConfig.menus[0].sections
      .flatMap((s: { items?: SectionItem[] }) => s.items || [])
      .filter((item: SectionItem) => {
        if (cartItemIds.has(item.id)) return false
        if (item.product_type !== 'ingredient') return false
        if (item.variation_price_overrides) return false
        const product = menuConfig?.products?.ingredients?.[item.product_id || '']
        if (product?.has_variations) return false
        return true
      })
      .map((item: SectionItem) => ({
        id: item.id,
        name: item.title,
        price: parseFloat(item.price || item.base_price || '0'),
        currency: item.currency,
        image_url: item.product_image_url,
      }))
  }, [menuConfig, items])

  const handleClose = () => {
    dispatch(closeCart())
  }

  const handleRemoveItem = (itemId: string) => {
    dispatch(removeItem(itemId))
  }

  const handleUpdateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      dispatch(removeItem(itemId))
    } else {
      dispatch(updateQuantity({ id: itemId, quantity }))
    }
  }

  const handleClearCart = useCallback(() => {
    const previousItems = [...items]
    dispatch(clearCart())
    toast('Cart cleared', {
      action: {
        label: 'Undo',
        onClick: () => {
          previousItems.forEach((item) => {
            dispatch(addItem({
              menuItem: item.menuItem,
              selectedModifiers: item.selectedModifiers,
              quantity: item.quantity,
              comboSelections: item.comboSelections,
              specialInstructions: item.specialInstructions,
            }))
          })
          toast.success('Cart restored')
        },
      },
    })
  }, [items, dispatch])

  const [isCheckingOut, setIsCheckingOut] = useState(false)

  const handleProceedToCheckout = async () => {
    if (items.length === 0 || isCheckingOut) return
    setIsCheckingOut(true)
    try {
      await onProceedToCheckout?.()
      dispatch(closeCart())
    } finally {
      setIsCheckingOut(false)
    }
  }

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[420px] !p-0 flex flex-col !bg-background overflow-hidden" hideCloseButton>
        {/* Header */}
        <SheetHeader className="px-5 py-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2.5 text-lg">
              <ShoppingCart className="h-5 w-5" />
              Your Cart
              <Badge variant="secondary" className="font-semibold">{totalItems}</Badge>
            </SheetTitle>
            <div className="flex items-center gap-3">
              {items.length > 0 && (
                <Button
                  variant="link"
                  className="h-auto p-0 text-sm text-muted-foreground hover:text-foreground"
                  onClick={handleClearCart}
                >
                  Clear all
                </Button>
              )}
              <button
                onClick={handleClose}
                aria-label="Close cart"
                className="w-10 h-10 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </SheetHeader>

        {/* Cart Items - Scrollable */}
        <div className="relative flex-1 min-h-0 overflow-hidden">
          <div className="h-full overflow-y-auto overscroll-contain px-5 py-4">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[280px] text-center">
                <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mb-5">
                  <ShoppingCart className="h-9 w-9 text-muted-foreground" />
                </div>
                <p className="text-lg font-semibold mb-1">Your cart is empty</p>
                <p className="text-sm text-muted-foreground">
                  Add items from the menu to get started
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {items.map((item, index) => (
                  <div key={item.id}>
                    <CartItem
                      item={item}
                      onQuantityChange={handleUpdateQuantity}
                      onRemove={handleRemoveItem}
                    />
                    {index < items.length - 1 && <Separator className="mt-5" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Suggestions */}
        {suggestionItems.length > 0 && items.length > 0 && (
          <div className="px-5 pb-4 border-t pt-4">
            <h3 className="font-semibold text-sm mb-3">You might also like</h3>
            <div className="overflow-x-auto scrollbar-hide">
              <div className="flex gap-3 pb-1">
                {suggestionItems.slice(0, 6).map((mi) => (
                  <div key={mi.id} className="shrink-0 w-[120px]">
                    <div className="h-20 w-full rounded-xl overflow-hidden bg-muted relative">
                      {mi.image_url && (
                        <img src={mi.image_url} alt={mi.name} className="w-full h-full object-cover" />
                      )}
                      <Button
                        variant="white"
                        size="icon"
                        className="absolute bottom-1.5 right-1.5 rounded-full h-7 w-7 shadow-sm border border-border"
                        onClick={() => dispatch(addItem({
                          menuItem: {
                            id: mi.id,
                            name: mi.name,
                            description: '',
                            price: mi.price,
                            currency: mi.currency,
                            image_url: mi.image_url || '',
                            category: '',
                            collection_id: '',
                            modifierGroups: [],
                            allergens: [],
                            foodTypes: [],
                            available: true,
                            itemType: 'single',
                          },
                          selectedModifiers: [],
                          quantity: 1,
                        }))}
                        aria-label={`Add ${mi.name}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="mt-1.5 text-xs font-medium truncate">{mi.name}</div>
                    <div className="text-xs text-muted-foreground">{formatPrice(mi.price, mi.currency)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer — single prominent checkout button */}
        {items.length > 0 && (
          <SheetFooter className="border-t px-5 py-4 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
            <Button
              variant="default"
              className="w-full h-14 rounded-xl text-base font-semibold"
              disabled={items.length === 0 || isCheckingOut}
              onClick={handleProceedToCheckout}
            >
              {isCheckingOut ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>Go to Checkout · {formatPrice(totals.total, items[0]?.menuItem?.currency)}</>
              )}
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  )
}
